<!-- Every act filed against this address, newest first. The acts themselves
     live in the akt module (a separate SPA at /akt), so the two buttons leave
     this app by href rather than by a router push — the same reason
     MainMenu.vue and Event.vue link out that way.

     The tab only exists while the module is switched on (see View.vue): with it
     off, /api/akt answers 404 like every other route behind whenAkt. -->
<template>
  <div :class="{ 'obj-card obj-table': plate }">
    <div v-if="plate" class="obj-card__head">
      <h2>Aktid <small>· {{ count }}</small></h2>
    </div>
    <v-data-table-server
      :headers="headers"
      :items="akts"
      :loading="loading"
      :items-length="count"
      v-model:items-per-page="itemsPerPage"
      v-model:page="page"
      :items-per-page-options="itemsPerPageOptions"
      items-per-page-text="Ridu lehel"
      no-data-text="Selle objektiga seotud akte pole"
      loading-text="Laen..."
    >
      <template v-slot:[`item.actDate`]="{item}">{{format(item.actDate)}}</template>
      <template v-slot:[`item.technicName`]="{item}">{{item.technicName || '—'}}</template>
      <template v-slot:[`item.sumTotal`]="{item}">{{money(item.sumTotal)}}</template>
      <template v-slot:[`item.status`]="{item}">
        <v-chip :color="state(item).color" size="x-small" variant="flat">{{state(item).text}}</v-chip>
      </template>
      <template v-slot:[`item.offerNo`]="{item}">
        <v-chip :color="offer(item).color" :title="item.offerNo || ''" size="x-small" variant="flat">
          {{offer(item).text}}
        </v-chip>
      </template>
      <!-- The arve Merit made of that offer, as the act holds it: found by the
           hourly sweep or written on the act by the office. Read from the row,
           so this column asks Merit nothing. -->
      <template v-slot:[`item.invoiceNo`]="{item}">{{item.invoiceNo || '—'}}</template>
      <template v-slot:[`item.actions`]="{item}">
        <!-- The PDF is written at signing, so a draft has none to offer. -->
        <v-btn
          v-if="item.hasPdf"
          :href="'/api/akt/' + item.id + '/pdf'"
          target="_blank"
          rel="noopener"
          color="primary"
          size="x-small"
          variant="text"
        >
          <v-icon size="x-small" start>mdi-file-pdf-box</v-icon>PDF
        </v-btn>
        <v-btn :href="'/akt/' + item.id" size="x-small" variant="text">Ava akt</v-btn>
      </template>
    </v-data-table-server>
  </div>
</template>

<script>
import api from '@/api'
import dayjs from 'dayjs'
import { mapActions, mapState } from 'vuex'

export default {
  props: {
    // The object page's new look (Seaded -> Objekt uues kujunduses): the same
    // content in the new card. overview is View.vue's, for the count.
    plate: {
      type: Boolean,
      default: false
    },
    overview: {
      type: Object,
      default: null
    },
    objectId: {
      required: true
    }
  },
  data () {
    return {
      akts: [],
      count: 0,
      page: 1,
      itemsPerPage: 10,
      loading: true,
      itemsPerPageOptions: [10, 20, 30]
    }
  },
  computed: {
    ...mapState(['features']),
    // The API orders by id descending and offers only date/technic sorting;
    // an address's history reads newest-first, so no header asks for more.
    headers () {
      return [{
        title: 'Kuupäev',
        align: 'start',
        value: 'actDate',
        sortable: false
      }, {
        title: 'Tehnik',
        align: 'start',
        value: 'technicName',
        sortable: false
      }, {
        title: 'Summa',
        align: 'end',
        value: 'sumTotal',
        sortable: false
      }, {
        title: 'Olek',
        align: 'start',
        value: 'status',
        sortable: false
      },
      // Pakkumised is a module of its own (Süsteem -> Seaded). With it off no
      // act can have an offer, so a column reading Saatmata for every row
      // would say nothing.
      ...(this.features.offer ? [{
        title: 'Merit',
        align: 'start',
        value: 'offerNo',
        sortable: false
      }, {
        title: 'Arve nr',
        align: 'start',
        value: 'invoiceNo',
        sortable: false
      }] : []),
      {
        title: '',
        value: 'actions',
        sortable: false,
        align: 'end'
      }]
    }
  },
  watch: {
    objectId () {
      this.refresh()
    },
    page () {
      this.refresh()
    },
    itemsPerPage () {
      this.page = 1
      this.refresh()
    }
  },
  methods: {
    ...mapActions(['showNotification']),
    async refresh () {
      // Leaving the page clears the route's objectId before this tab is gone.
      if (!this.objectId) {
        return
      }
      this.loading = true
      try {
        const { akts, total } = await api.getObjectAkts(this.objectId, {
          limit: this.itemsPerPage,
          offset: (this.page - 1) * this.itemsPerPage
        })
        this.akts = akts
        this.count = total
      } catch (err) {
        this.showNotification({ text: err.message, color: 'red' })
      }
      this.loading = false
    },
    format (date) {
      return date ? dayjs(date).format('DD.MM.YYYY') : '—'
    },
    money (v) {
      return (v === null || v === undefined || v === '') ? '0.00' : Number(v).toFixed(2)
    },
    // Mirrors the act's own flags: signed is not finished until it has been
    // sent, and a reopened act is a draft again — but a draft with a history,
    // which is worth saying rather than hiding.
    //
    // "Lõpetatud", not "Saadetud": an act is finished by an e-mail to the
    // customer *and* its data going to Merit, so a row that has done only one
    // of the two still reads "Allkirjastatud" — the Merit column beside it is
    // what says which half is missing.
    //
    // "Kinnitatud" sits above it: the office's own finish line, reached when
    // Merit turned the act's pakkumine into an arve or an admin closed the act
    // by hand. A confirmed act is final — it cannot be reopened.
    state (akt) {
      if (akt.isConfirmed) {
        return { text: 'Kinnitatud', color: 'green-darken-3' }
      }
      if (akt.isDone) {
        return { text: 'Lõpetatud', color: 'green' }
      }
      if (akt.isSigned) {
        return { text: 'Allkirjastatud', color: 'blue' }
      }
      if (akt.reopenedAt) {
        return { text: 'Avatud uuesti', color: 'orange-lighten-4' }
      }
      return { text: 'Mustand', color: 'grey-lighten-2' }
    },
    // Whether this act has been turned into a Merit pakkumine. The act keeps
    // the offer number once it has one, so the row is the whole answer — the
    // number itself is on the chip's title rather than in the column, which
    // has one word's worth of room.
    offer (akt) {
      return akt.offerNo
        ? { text: 'Saadetud', color: 'green' }
        : { text: 'Saatmata', color: 'grey-lighten-2' }
    }
  },
  mounted () {
    this.refresh()
  }
}
</script>
